import type {
  CustomField,
  CustomFieldGroup,
  CustomFieldGroupDefinition,
  Resource
} from '__types__';
import { resolveCustomFieldValue } from 'v5/core/custom-fields/resolveCustomFieldValue';
import { valueScopeUtils } from 'v5/core/form-schema/formSchema.types';
import type {
  CodecContext,
  RowTarget
} from 'v5/core/entity-values/values.types';

type GroupRowTarget = Extract<RowTarget, { type: 'custom-field-group-row' }>;

/** Group rows in the person's order: `order` ascending (nulls last), then id. */
export function compareGroupRows(
  a: CustomFieldGroup,
  b: CustomFieldGroup
): number {
  const ao = a.order ?? Number.POSITIVE_INFINITY;
  const bo = b.order ?? Number.POSITIVE_INFINITY;
  return ao - bo || a.id - b.id;
}

/**
 * Live rows of one group definition in their order (`compareGroupRows`) —
 * the index space of `RowTarget.custom-field-group-row`.
 */
export function liveGroupRows(
  entity: Resource,
  definition: CustomFieldGroupDefinition
): CustomFieldGroup[] {
  return (entity.custom_fieldgroups ?? [])
    .filter(
      row =>
        !row.archived && row.custom_fieldgroup_definition_id === definition.id
    )
    .sort(compareGroupRows);
}

/**
 * Member row of a group row for one definition. Groups ignore scope. A live
 * member wins; an archived one is returned only when `includeArchived`
 * (write path: reuse the slot instead of inserting a duplicate).
 */
export function findGroupMemberRow(
  groupRow: CustomFieldGroup,
  definitionId: number,
  includeArchived: boolean
): CustomField | null {
  const members = (groupRow.custom_fields ?? []).filter(
    member => member.custom_field_definition_id === definitionId
  );
  const live = members.find(member => !member.archived) ?? null;
  if (live !== null) return live;
  return includeArchived ? (members[0] ?? null) : null;
}

function groupRowAt(
  entity: Resource,
  target: GroupRowTarget
): CustomFieldGroup | null {
  return liveGroupRows(entity, target.definition)[target.index] ?? null;
}

/**
 * Row a codec decodes from. Entity target: workspace row preferred, Global
 * fallback (the "inherited until overridden" lens). Group target: the live
 * member of the addressed group row.
 */
export function findCustomFieldRowForRead(
  entity: Resource,
  definitionId: number,
  ctx: CodecContext
): CustomField | null {
  switch (ctx.target.type) {
    case 'entity':
      return resolveCustomFieldValue(
        entity.custom_fields,
        definitionId,
        valueScopeUtils.toWorkspaceId(ctx.scope)
      );
    case 'custom-field-group-row': {
      const row = groupRowAt(entity, ctx.target);
      return row === null ? null : findGroupMemberRow(row, definitionId, false);
    }
    case 'rates-item':
      throw new Error(
        `Custom field ${definitionId} cannot be read from a rates item`
      );
    case 'locations-item':
      throw new Error(
        `Custom field ${definitionId} cannot be read from a locations item`
      );
  }
}
